import { ref } from 'vue';
import { defineStore } from 'pinia';
import { getProducts } from '../utils/api';

const useProductFinderStore = defineStore('productFinder', () => {
  // Store.
  const crops = ref([]);
  const applications = ref([]);
  const targetAreas = ref([]);
  const products = ref([]);
  const selectedCrops = ref([]);
  const selectedApplications = ref([]);
  const selectedTargetAreas = ref([]);
  const sustainableApplicationFilters = ref({
    greenhouseGasEfficiency: false,
    waterEfficiency: false,
    nutrientUseEfficiency: false,
  });
  const sustainableProductionFilters = ref({
    co2Reduction: false,
  });
  const showOnlyOrganic = ref(false);
  const transitionName = ref('products-fade-in');

  // Getters.
  const getMainCrops = () => crops.value.filter(({ parent }) => parent === 0);

  const getCropsByParent = (parentId) => {
    return crops.value.filter((crop) => crop.parent === parentId);
  };

  const getActiveFiltersCount = () => {
    // Organic farming filter is not included as it is not really a boolean filter.
    const booleanFiltersCount = Object.values({
      ...sustainableApplicationFilters.value,
      ...sustainableProductionFilters.value,
    }).filter((val) => val).length;

    return (
      selectedCrops.value.length +
      selectedApplications.value.length +
      selectedTargetAreas.value.length +
      booleanFiltersCount
    );
  };

  const getFilteredProducts = () => {
    // Return sorted products if no filters are applied.
    if (getActiveFiltersCount() === 0) {
      return [...products.value].sort((a, b) => {
        return a.name.localeCompare(b.name);
      });
    }

    // Get the filtered products and sort them alphabetically.
    const filteredProducts = products.value.filter((product) => {
      return (
        (selectedCrops.value.length > 0
          ? selectedCrops.value.every((c) => product.crops.includes(c))
          : true) &&
        (selectedApplications.value.length > 0
          ? selectedApplications.value.every((a) =>
              product.applications.includes(a),
            )
          : true) &&
        (selectedTargetAreas.value.length > 0
          ? selectedTargetAreas.value.every((ta) =>
              product.targetAreas.includes(ta),
            )
          : true) &&
        // In a boolean filters group, if any filter is truthy, collect all truthy filters of the group.
        (Object.values(sustainableApplicationFilters.value).some((val) => val)
          ? Object.keys(sustainableApplicationFilters.value)
              .filter((key) => sustainableApplicationFilters.value[key])
              .every((key) => product.sustainableApplication[key] === true)
          : true) &&
        (Object.values(sustainableProductionFilters.value).some((val) => val)
          ? Object.keys(sustainableProductionFilters.value)
              .filter((key) => sustainableProductionFilters.value[key])
              .every((key) => product.sustainableProduction[key] === true)
          : true)
      );
    });

    return filteredProducts.sort((a, b) => a.name.localeCompare(b.name));
  };

  // Actions.
  const initStore = async (lang) => {
    const { data } = await getProducts(lang);
    crops.value = data.crops;
    applications.value = data.applications;
    targetAreas.value = data.targetAreas;
    products.value = data.products;
  };

  const resetFilters = () => {
    selectedCrops.value = [];
    selectedApplications.value = [];
    selectedTargetAreas.value = [];
    sustainableApplicationFilters.value = Object.fromEntries(
      Object.entries(sustainableApplicationFilters.value).map(([key, val]) => [
        key,
        val ? !val : val,
      ]),
    );
    sustainableProductionFilters.value = Object.fromEntries(
      Object.entries(sustainableProductionFilters.value).map(([key, val]) => [
        key,
        val ? !val : val,
      ]),
    );
    showOnlyOrganic.value = false;
  };

  return {
    crops,
    applications,
    targetAreas,
    products,
    selectedCrops,
    selectedApplications,
    selectedTargetAreas,
    sustainableApplicationFilters,
    sustainableProductionFilters,
    showOnlyOrganic,
    transitionName,
    getMainCrops,
    getCropsByParent,
    getActiveFiltersCount,
    getFilteredProducts,
    initStore,
    resetFilters,
  };
});

export default useProductFinderStore;
